import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import type { Map as MapboxMap, MapMouseEvent, MapTouchEvent, PointLike } from 'mapbox-gl';

/**
 * DOM-overlay vertex + midpoint handles for the turf-cutting UI.
 *
 * Why not just style Mapbox Draw's built-in symbol layers?
 *   • Handles need CSS transitions for hover / drag — a WebGL raster
 *     can't do that.
 *   • Colour must theme per subsidiary via CSS custom properties (--color-*),
 *     which a baked icon image can't read.
 *   • The hit target (44 / 36 px) must be larger than the visible disk
 *     (26 / 14 px) so older users with a shaky pointer still land the drag.
 *     A single icon image can only be both the visual and the hit target.
 *
 * How it stays in sync with Draw's native drag:
 *   • Draw's own vertex/midpoint layers are still registered by
 *     turfDrawStyles.ts, but with `circle-opacity: 0` and radii sized to the
 *     hit target — invisible-but-hit-testable. Draw's internal
 *     queryRenderedFeatures on those layers picks up mousedowns and runs its
 *     usual drag machinery.
 *   • This component renders visible `<div>` disks on top with
 *     `pointer-events: none`, so all pointer input falls through to Draw.
 *   • Marker positions are recomputed on every map `render` event, so during
 *     a drag the visible disk tracks the underlying vertex frame-by-frame.
 *   • Hover + dragging state is inferred: hover from a `mousemove` +
 *     queryRenderedFeatures on the vertex layer, dragging from a
 *     `mousedown` that hits the vertex layer, cleared on window `mouseup`.
 *
 * Accessibility: handles are `aria-hidden` decorative divs. The overlay is
 * purely visual for pointer/touch users; keyboard vertex editing would need
 * real onKeyDown handlers and is out of scope for v1.
 */

interface TurfVertexOverlayProps {
  map: MapboxMap | null;
  enabled: boolean;
}

interface Handle {
  id: string;
  kind: 'vertex' | 'midpoint';
  x: number;
  y: number;
  coordPath: string;
  parent: string;
}

// Mapbox Draw duplicates each source-less style into `.hot` and `.cold`
// layers (one per internal source). We hit-test all four for handles, and
// the polygon-fill pair for whole-shape drags.
const HANDLE_HIT_LAYERS = [
  'gl-draw-polygon-and-line-vertex-active.hot',
  'gl-draw-polygon-and-line-vertex-active.cold',
  'gl-draw-polygon-midpoint.hot',
  'gl-draw-polygon-midpoint.cold',
];
const POLYGON_FILL_HIT_LAYERS = [
  'gl-draw-polygon-fill-active.hot',
  'gl-draw-polygon-fill-active.cold',
];
const HOT_SOURCE = 'mapbox-gl-draw-hot';
const OVERLAY_CLASS = 'turf-vertex-overlay-container';

type DragState = { kind: 'vertex'; id: string } | { kind: 'polygon' } | null;

/**
 * Hit-test only the layers Draw has actually registered. Draw adds its
 * .hot/.cold layers lazily — before the first shape exists none of them are in
 * the style, and mapbox-gl throws on a query naming a missing layer, so an
 * unguarded call fires on every mousemove and mousedown during turf cutting.
 */
function queryExistingLayers(
  map: MapboxMap,
  point: PointLike,
  layers: string[],
) {
  const present = layers.filter((id) => map.getLayer(id));
  if (!present.length) return [];
  return map.queryRenderedFeatures(point, { layers: present });
}

/** Stable id so React reuses the same DOM element (and its hover state) across renders. */
function handleId(kind: 'vertex' | 'midpoint', parent: string, coordPath: string): string {
  return `${kind}:${parent}:${coordPath}`;
}

interface ParsedHandle {
  kind: 'vertex' | 'midpoint';
  parent: string;
  coordPath: string;
  id: string;
}

/** Pull kind/parent/coord_path off a Draw feature; null if any piece is missing. */
function parseHandleFeature(feature: {
  properties?: Record<string, unknown> | null;
}): ParsedHandle | null {
  const meta = feature.properties?.meta;
  if (meta !== 'vertex' && meta !== 'midpoint') return null;
  const parent = String(feature.properties?.parent ?? '');
  const coordPath = String(feature.properties?.coord_path ?? '');
  if (!parent || !coordPath) return null;
  return { kind: meta, parent, coordPath, id: handleId(meta, parent, coordPath) };
}

export function TurfVertexOverlay({ map, enabled }: TurfVertexOverlayProps) {
  const [handles, setHandles] = useState<Handle[]>([]);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [drag, setDrag] = useState<DragState>(null);
  // Container lives in state, not a ref, so the portal renders as soon as the
  // container is inserted into the map — a ref alone wouldn't schedule a
  // re-render, leaving the portal empty until some later setState happens.
  const [container, setContainer] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!map || !enabled) return;
    const parent = map.getContainer();
    const el = document.createElement('div');
    el.className = OVERLAY_CLASS;
    parent.appendChild(el);
    setContainer(el);
    return () => {
      if (el.parentNode) el.parentNode.removeChild(el);
      setContainer(null);
    };
  }, [map, enabled]);

  // Recompute handle positions on every render frame — this is what keeps
  // the visible disk glued to the underlying vertex during pan/zoom/drag.
  useEffect(() => {
    if (!map || !enabled) {
      setHandles([]);
      return;
    }
    // Two-var pattern: `scheduled` is the reentry guard (bool avoids
    // false-positives when rAF returns id 0), `rafId` is only for cancellation.
    let scheduled = false;
    let rafId: number | null = null;
    const recompute = () => {
      if (scheduled) return;
      scheduled = true;
      rafId = requestAnimationFrame(() => {
        scheduled = false;
        rafId = null;
        const features = map.querySourceFeatures(HOT_SOURCE, {}) as unknown as Array<{
          geometry: { type: string; coordinates: [number, number] };
          properties: Record<string, unknown> | null;
        }>;
        const next: Handle[] = [];
        for (const f of features) {
          if (f.geometry?.type !== 'Point') continue;
          const parsed = parseHandleFeature(f);
          if (!parsed) continue;
          const [lng, lat] = f.geometry.coordinates;
          const { x, y } = map.project([lng, lat]);
          next.push({
            id: parsed.id,
            kind: parsed.kind,
            x,
            y,
            coordPath: parsed.coordPath,
            parent: parsed.parent,
          });
        }
        // Dedupe by id (Draw sometimes emits the same vertex twice while dragging)
        const seen = new Set<string>();
        const deduped = next.filter((h) => (seen.has(h.id) ? false : (seen.add(h.id), true)));
        setHandles(deduped);
      });
    };
    recompute();
    map.on('render', recompute);
    map.on('sourcedata', recompute);
    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      map.off('render', recompute);
      map.off('sourcedata', recompute);
    };
  }, [map, enabled]);

  // Hover: query the vertex layer at the mouse point, throttled to rAF.
  // Modern trackpads/gaming mice fire mousemove at 500-1000Hz; without the
  // throttle we'd run queryRenderedFeatures that many times per second, which
  // is measurable JS + Mapbox spatial-index work for zero visible benefit
  // (the screen only paints at 60/120Hz anyway). Coalescing to one hit-test
  // per animation frame keeps hover snappy while cutting the query rate 8-16x.
  useEffect(() => {
    if (!map || !enabled) return;
    let scheduled = false;
    let rafId: number | null = null;
    let latestPoint: MapMouseEvent['point'] | null = null;
    const onMove = (e: MapMouseEvent) => {
      latestPoint = e.point;
      if (scheduled) return;
      scheduled = true;
      rafId = requestAnimationFrame(() => {
        scheduled = false;
        rafId = null;
        if (!latestPoint) return;
        const hits = queryExistingLayers(map, latestPoint, HANDLE_HIT_LAYERS);
        if (!hits.length) {
          setHoveredId((prev) => (prev === null ? prev : null));
          return;
        }
        const parsed = parseHandleFeature(hits[0]);
        if (!parsed) return;
        setHoveredId((prev) => (prev === parsed.id ? prev : parsed.id));
      });
    };
    map.on('mousemove', onMove);
    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      map.off('mousemove', onMove);
    };
  }, [map, enabled]);

  // Drag detection: on mousedown, decide what's being grabbed.
  //   • Handle hit (vertex or midpoint) → single-vertex drag. The grabbed
  //     handle takes the "held" treatment (grow + accent glow, same as hover);
  //     no colour flip, because the cursor moving alongside the vertex is
  //     already unambiguous feedback.
  //   • Polygon-fill hit → whole-polygon drag. EVERY vertex handle flips to
  //     the accent colour, because "the whole shape is being moved" is not
  //     otherwise visually communicated.
  //   • Window mouseup / touchend ends whichever mode is active.
  useEffect(() => {
    if (!map || !enabled) return;
    const startDrag = (point: { x: number; y: number }) => {
      const handleHits = queryExistingLayers(map, [point.x, point.y], HANDLE_HIT_LAYERS);
      if (handleHits.length) {
        const parsed = parseHandleFeature(handleHits[0]);
        if (!parsed) return;
        // Midpoints become vertices on grab — track the id as a vertex so the
        // freshly-materialized vertex takes the held treatment.
        setDrag({ kind: 'vertex', id: handleId('vertex', parsed.parent, parsed.coordPath) });
        return;
      }
      const polyHits = queryExistingLayers(map, [point.x, point.y], POLYGON_FILL_HIT_LAYERS);
      if (polyHits.length) {
        setDrag({ kind: 'polygon' });
      }
    };
    const onMouseDown = (e: MapMouseEvent) => startDrag(e.point);
    const onTouchStart = (e: MapTouchEvent) => startDrag(e.point);
    const onUp = () => setDrag(null);
    map.on('mousedown', onMouseDown);
    map.on('touchstart', onTouchStart);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchend', onUp);
    return () => {
      map.off('mousedown', onMouseDown);
      map.off('touchstart', onTouchStart);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchend', onUp);
    };
  }, [map, enabled]);

  // Vertices render above midpoints so a click on the overlap zone hits a
  // vertex first (matches the spec's z-order requirement).
  const sortedHandles = useMemo(() => {
    return [...handles].sort((a, b) => {
      if (a.kind === b.kind) return 0;
      return a.kind === 'vertex' ? 1 : -1;
    });
  }, [handles]);

  if (!container || !enabled) return null;

  return createPortal(
    <>
      {sortedHandles.map((h) => {
        const isHeld = drag?.kind === 'vertex' && drag.id === h.id;
        const isPolygonDrag = drag?.kind === 'polygon';
        const isHovered = hoveredId === h.id;
        const cls = [
          'turf-handle',
          `turf-handle--${h.kind}`,
          // vertex-drag reuses the hover treatment (grow + glow) — cursor
          // motion already tells you which vertex you have
          (isHeld || (isHovered && !drag)) && 'turf-handle--hovered',
          // polygon-drag: every vertex flips accent so "moving the whole
          // shape" reads at a glance
          isPolygonDrag && 'turf-handle--polygon-dragging',
        ]
          .filter(Boolean)
          .join(' ');
        return (
          <div
            key={h.id}
            aria-hidden="true"
            data-kind={h.kind}
            data-handle-id={h.id}
            className={cls}
            style={{ left: h.x, top: h.y }}
          />
        );
      })}
    </>,
    container,
  );
}

export default TurfVertexOverlay;
